import { FC } from 'react';
import { State } from 'redux/initialState';
import { useSelector } from 'react-redux';
import { useTranslate } from 'hooks/useTranslate';
import Accordion from 'react-bootstrap/Accordion';
import Button from 'react-bootstrap/Button';

import { SocietyTypes } from 'types/Society';
import { hasUnlimitedVersion } from 'utils/hasUnlimitedVersion';

import { societyHasTitles } from 'utils/societyHasTitles';
import MenuItem from './MenuItem';
import DilutionBadge from '../MenuDilutionBadge';

type Props = {
  order: string;
  societyId: string;
  userId?: string;
  isAdmin: boolean;
  isPartner: boolean;
  isInvestor: boolean;
  hasAccessCaptable: boolean;
  isActive: boolean;
};

const PartnersMenu: FC<Props> = ({
  order,
  societyId,
  userId,
  isAdmin,
  isPartner,
  isInvestor,
  hasAccessCaptable,
  isActive,
}) => {
  const { t } = useTranslate();

  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );

  const isAssocSociety = actualSociety?.societyType === SocietyTypes.ASSOC;

  const hasMyCardLinkDisabled =
    (!isAdmin && (!isPartner || !hasAccessCaptable)) || (isAdmin && !isPartner);
  const hasPartnersLinkDisabled = !isAdmin && !isPartner;
  const hasPartnerBookLinkDisabled =
    (!isAdmin && (!isPartner || !hasAccessCaptable)) || isAssocSociety;
  const hasPartnersCardsLinkDisabled =
    (!isAdmin && !isPartner) || isAssocSociety;
  const hasInvestorsLinkDisabled = (!isAdmin && !isInvestor) || isAssocSociety;
  const hasRepresentantsLinkDisabled = !isAdmin;
  const hasHoldingClassesLinkDisabled = !isAdmin || isAssocSociety;
  const hasPartnerCategoriesLinkDisabled = !isAdmin || isAssocSociety;
  const hasTitlesLinkDisabled = !societyHasTitles(actualSociety) || !isAdmin;

  return (
    <>
      <Accordion.Toggle as={Button} variant="link" eventKey={order}>
        <li className={`nk-menu-item has-sub ${isActive ? 'active' : ''}`}>
          <a className="nk-menu-link nk-menu-toggle">
            <span className="nk-menu-icon">
              <em className="icon ni ni-users" />
            </span>
            <span className="nk-menu-text">{t('Partners')}</span>
            <DilutionBadge />
          </a>
        </li>
      </Accordion.Toggle>

      <Accordion.Collapse eventKey={order}>
        <ul className="nk-menu-sub">
          {!hasMyCardLinkDisabled && (
            <MenuItem linkTo={`/socios/${societyId}/${userId}`} text="MyCard" />
          )}

          {!hasPartnersLinkDisabled && (
            <MenuItem linkTo={`/socios/${societyId}`} text="Partners" />
          )}

          {!hasPartnerBookLinkDisabled && (
            <MenuItem
              linkTo={`/socios-libro/${societyId}`}
              text="PartnerBook"
            />
          )}

          {!hasTitlesLinkDisabled && (
            <MenuItem
              linkTo={`/titulos-libro/${societyId}`}
              text="TitleRegistryBook"
            />
          )}

          {!hasPartnersCardsLinkDisabled && (
            <MenuItem
              linkTo={`/socios-fichas/${societyId}`}
              text="PartnersCards"
            />
          )}

          {!hasInvestorsLinkDisabled && (
            <MenuItem
              linkTo={`/inversores/${societyId}`}
              text="Investors"
              disabled={!hasUnlimitedVersion()}
            />
          )}

          {!hasRepresentantsLinkDisabled && (
            <MenuItem
              linkTo={`/socios-representantes/${societyId}`}
              text="Representants"
            />
          )}

          {!hasHoldingClassesLinkDisabled && (
            <MenuItem
              linkTo={`/clases-participaciones/${societyId}`}
              text="HoldingClasses"
            />
          )}

          {!hasPartnerCategoriesLinkDisabled && (
            <MenuItem
              linkTo={`/socios-categorias/${societyId}`}
              text="PartnerCategories"
            />
          )}
        </ul>
      </Accordion.Collapse>
    </>
  );
};

export default PartnersMenu;
